import React, { useState, useContext } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    InputAdornment,
    Avatar,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, LockOutlined, LockReset } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import AuthContext from '../context/AuthContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackSeverity, setSnackSeverity] = useState('success');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleLogin = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/users/login`, { email, password });
            login(response.data.user, response.data.token);
            setSnackSeverity('success');
            setSnackMessage('Login successful');
            setSnackOpen(true);
            navigate('/');
        } catch (err) {
            setSnackSeverity('error');
            setSnackMessage(err.response?.data?.message || 'Login failed');
            setSnackOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetPassword = async () => {
        try {
            const response = await axios.post(`${API_BASE_URL}/users/reset-password`, { email: resetEmail });
            setSnackSeverity('success');
            setSnackMessage(response.data.message || 'Password reset email sent');
            setSnackOpen(true);
            setDialogOpen(false);
        } catch (err) {
            setSnackSeverity('error');
            setSnackMessage(err.response?.data?.message || 'Failed to reset password');
            setSnackOpen(true);
        }
    };

    const handleSnackClose = () => {
        setSnackOpen(false);
    };

    const openResetDialog = () => {
        setDialogOpen(true);
    };

    const closeResetDialog = () => {
        setDialogOpen(false);
    };

    const confirmResetPassword = () => {
        if (!resetEmail) {
            setEmailError('Email address is required');
            return;
        }
        closeResetDialog();
        handleResetPassword();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.65) 0%, rgba(30, 41, 59, 0.45) 100%), url('/images/login-bg.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                p: 2,
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    borderRadius: 4,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    backdropFilter: 'blur(16px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.94)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Avatar
                            sx={{
                                m: 1,
                                bgcolor: 'primary.main',
                                width: 56,
                                height: 56,
                                boxShadow: '0 8px 20px rgba(255, 165, 0, 0.4)',
                            }}
                        >
                            <LockOutlined sx={{ fontSize: 30, color: '#ffffff' }} />
                        </Avatar>
                        <Typography
                            variant="h4"
                            color="primary"
                            gutterBottom
                            align="center"
                            sx={{
                                fontWeight: 700,
                                mt: 1,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Login to MSB ERP
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Enter your credentials to access your account
                        </Typography>
                    </Box>

                    <Box component="form" noValidate sx={{ mt: 1 }}>
                        <TextField
                            label="Email"
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    '& .MuiSvgIcon-root': {
                                        color: '#64748b',
                                        opacity: 1,
                                        visibility: 'visible !important',
                                        transition: 'color 0.2s ease',
                                    },
                                    '&:hover .MuiSvgIcon-root': {
                                        color: '#FFA500',
                                        opacity: 1,
                                        visibility: 'visible !important',
                                    },
                                    '&.Mui-focused .MuiSvgIcon-root': {
                                        color: '#FFA500',
                                        opacity: 1,
                                        visibility: 'visible !important',
                                    },
                                },
                            }}
                        />
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined sx={{ fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={togglePasswordVisibility}
                                            edge="end"
                                            size="small"
                                            sx={{
                                                color: '#64748b',
                                                '&:hover': {
                                                    color: '#FFA500',
                                                    backgroundColor: 'rgba(255, 165, 0, 0.08)',
                                                },
                                            }}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    '& .MuiSvgIcon-root': {
                                        color: '#64748b',
                                        opacity: 1,
                                        visibility: 'visible !important',
                                        transition: 'color 0.2s ease',
                                    },
                                    '&:hover .MuiSvgIcon-root': {
                                        color: '#FFA500',
                                        opacity: 1,
                                        visibility: 'visible !important',
                                    },
                                    '&.Mui-focused .MuiSvgIcon-root': {
                                        color: '#FFA500',
                                        opacity: 1,
                                        visibility: 'visible !important',
                                    },
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            disabled={isSubmitting}
                            sx={{
                                mt: 3,
                                py: 1.4,
                                fontSize: '1rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: 2.5,
                                boxShadow: '0 8px 20px rgba(255, 165, 0, 0.35)',
                                '&:hover': {
                                    boxShadow: '0 12px 24px rgba(255, 165, 0, 0.45)',
                                },
                            }}
                            onClick={handleLogin}
                        >
                            {isSubmitting ? 'Logging in...' : 'Login'}
                        </Button>

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                variant="text"
                                color="primary"
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 165, 0, 0.08)',
                                    },
                                }}
                                onClick={openResetDialog}
                            >
                                Reset Password
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Snackbar
                open={snackOpen}
                autoHideDuration={6000}
                onClose={handleSnackClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackClose}
                    severity={snackSeverity}
                    sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}
                >
                    {snackMessage}
                </Alert>
            </Snackbar>

            <Dialog
                open={dialogOpen}
                onClose={closeResetDialog}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1,
                        maxWidth: 420,
                        width: '100%',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, pb: 0 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', mr: 2 }}>
                        <LockReset />
                    </Avatar>
                    <DialogTitle sx={{ color: 'primary.main', p: 0, fontWeight: 700, fontSize: '1.25rem' }}>
                        Reset Password
                    </DialogTitle>
                </Box>
                <DialogContentText sx={{ px: 3, pt: 1, pb: 0, color: 'text.secondary', fontSize: '0.875rem' }}>
                    To reset your password, please enter your email address here. We
                    will send a new password to your email.
                </DialogContentText>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={resetEmail}
                        onChange={(e) => {
                            setResetEmail(e.target.value);
                            setEmailError('');
                        }}
                        error={!!emailError}
                        helperText={emailError}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeResetDialog} color="inherit" sx={{ fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmResetPassword}
                        variant="contained"
                        color="primary"
                        sx={{ fontWeight: 600, borderRadius: 2 }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Login;
